import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { FinancialTransactionStatus } from '@ankor-io/common/types/awx/wallet/balance'

/** Report types we generate and store ourselves (not AWX-generated). */
export enum GeneratedReportType {
  XERO_BANK_STATEMENT = 'XERO_BANK_STATEMENT',
  EXPENSE_REPORT = 'EXPENSE_REPORT',
  // Kept as a distinct type (vs. a flag on EXPENSE_REPORT) so each branch of the DO
  // can select a different upload strategy — single-file vs. multipart ZIP.
  EXPENSE_REPORT_WITH_RECEIPTS = 'EXPENSE_REPORT_WITH_RECEIPTS',
  // more report types will be added here in the future...
}

/** Represents the current status of the report generation process */
export enum GeneratedReportStatus {
  PENDING = 'PENDING',
  READY = 'READY',
  FAILED = 'FAILED',
  // EXPIRED status is not needed, as we dont return expired reports
}

/** Backend type */
export type GeneratedReport = {
  id: string
  organizationId: string
  vesselId: string
  awxConnectedAccountId: string
  fromDate: string
  toDate: string
  type: GeneratedReportType
  status: GeneratedReportStatus
  fileName: string
  storageKey: string
  errorMessage?: string
  createdBy: string
  expiresAt: string
  createdAt: string
  updatedAt: string
}

/** Payload returned when listing generated reports */
export type GeneratedReportPayload = {
  id: string
  type: GeneratedReportType
  /** the status of the report (PENDING, READY, FAILED) */
  status: GeneratedReportStatus
  fileName: string
  fromDate: string
  toDate: string
  errorMessage?: string
  /** user ID who triggered the generation */
  createdBy: string
  expiresAt: string
  createdAt: string
}

export type CreateGeneratedReportRequest = {
  id: string
  organizationId: string
  vesselId: string
  awxConnectedAccountId: string
  fromDate: string
  toDate: string
  type: GeneratedReportType
  status: GeneratedReportStatus
  fileName: string
  storageKey: string
  errorMessage?: string
  createdBy: string
  expiresAt: string
}

export type ListGeneratedReportsPayload = {
  items: GeneratedReportPayload[]
  /** Whether there are more pages of results */
  hasMore: boolean
}

/** Internal-only request used by background generation to update report lifecycle state. */
export type UpdateGeneratedReportStatusRequest = {
  id: string
  status: GeneratedReportStatus
  errorMessage?: string
}

export type ListGeneratedReportsRequest = {
  organizationId: string
  vesselId?: string
  awxConnectedAccountId?: string
  /** Optional filter for report lifecycle statuses (PENDING, READY, FAILED). */
  statuses?: GeneratedReportStatus[]
  /** Optional filter for report types (e.g. XERO_BANK_STATEMENT, EXPENSE_REPORT). */
  reportTypes?: GeneratedReportType[]
  /** Optional lower bound for the `expiresAt` timestamp; pass the current time to return only non-expired reports. */
  expiresAfter?: string
  take?: number
  skip?: number
}

/** Last run summary included in report generation job listings. */
export type ReportGenerationJobLastRun = {
  id: string
  status: string
  createdAt: string
  completedAt?: string
}

/** Lighthouse payload for a report generation job (a generated report + its latest pipeline run). */
export type ReportGenerationJobPayload = {
  id: string
  type: GeneratedReportType
  status: GeneratedReportStatus
  organizationId: string
  vesselId: string
  awxConnectedAccountId: string
  fileName: string
  fromDate: string
  toDate: string
  errorMessage?: string
  createdBy: string
  expiresAt: string
  /** When the report generation job was created (i.e. when generation was triggered). */
  startedAt: string
  finishedAt?: string
  duration?: number
  requiredStages: ReportGenerationStage[]
  lastRun?: ReportGenerationJobLastRun
}

export type GenerateXeroBankStatementBody = {
  fromDate: string
  toDate: string
  /** Optional — filter to one or more transaction statuses. Defaults to [SETTLED]. */
  statuses?: FinancialTransactionStatus[]
}

export type GenerateExpenseReportBody = {
  /** UTC day start, e.g. "2026-01-01T00:00:00Z" */
  fromDate: string
  /** UTC day end, e.g. "2026-03-31T23:59:59Z" */
  toDate: string
  /** Optional — filter to one charter; APA connected accounts only */
  charterId?: string
  /** Whether to include attachments */
  includeAttachments: boolean
}

export type ExpenseReportPipelineParams = GenerateExpenseReportBody & {
  awxConnectedAccountId: string
  connectedAccountType: AwxConnectedAccountType
}

/** Shape returned by the pay-gateway ListActivityEventDetailsByIds query.
 * Returned rows are limited to activity events with card or deposit details.
 * Each event has either cardActivityEvent or depositActivityEvent populated (not both). */
export type RawActivityEventDetails = {
  id: string
  cardActivityEvent: {
    merchant_name: string
    merchant_code: string
    merchant_city: string
    merchant_country: string
    transaction_amount_cents: number
    transaction_currency_code: string
    billing_amount_cents: number
    billing_currency_code: string
    card: { card_number: string | null; nick_name: string }
  } | null
  depositActivityEvent: {
    reference: string | null
  } | null
  conversionActivityEvent: {
    awx_rate: string
    buy_amount_currency_code: string
    sell_amount_currency_code: string
    conversion_id: string
  } | null
  transferActivityEvent: {
    reference: string | null
    external_reference: string | null
    beneficiary_id: string
    transfer_id: string
  } | null
}

export enum ReportGenerationStage {
  PREPARE_DATA = 'PREPARE_DATA',
  TRANSFORM_DATA = 'TRANSFORM_DATA',
  UPLOAD_REPORT = 'UPLOAD_REPORT',
  // future stages for other report types could look like:
  // FETCH_ATTACHMENTS = 'FETCH_ATTACHMENTS',
  // RENDER_PDF = 'RENDER_PDF',
}

export enum ReportGenerationStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export type ReportGenerationStageState = {
  status: ReportGenerationStatus
  /** Error message if the stage failed. Surfaced in the Lighthouse run-pipeline UI. */
  issue?: string
}

export type ReportGenerationPipelineData = {
  stages: Partial<Record<ReportGenerationStage, ReportGenerationStageState>>
}

export type ReportGenerationRunSteps = 'PREPARE_DATA' | 'TRANSFORM' | 'UPLOAD'

export type ReportGenerationRunStepPrepareDataData = {
  message: string
  /** Number of raw items loaded from cached source. */
  rawItemCount?: number
  /** Number after filtering/validation. */
  filteredItemCount?: number
  /** Number of unique ids used for enrichment lookup. */
  enrichmentLookupCount?: number
  details?: { [key: string]: unknown }
}

export type ReportGenerationRunStepTransformData = {
  message: string
  /** Number of output files generated (e.g. per-currency CSVs). */
  outputFileCount?: number
  /** Size of the output artifact in bytes. */
  artifactBytes?: number
  details?: { [key: string]: unknown }
}

export type ReportGenerationRunStepUploadData = {
  message: string
  /** R2 storage path the artifact was written to. */
  storagePath?: string
  /** Size uploaded in bytes. */
  uploadedBytes?: number
  details?: { [key: string]: unknown }
}
